import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Pricing from './pages/Pricing';
import Services from './pages/Services';
import CheckoutTesting from './pages/services/CheckoutTesting';
import PerformanceTesting from './pages/services/PerformanceTesting';
import IntegrationTesting from './pages/services/IntegrationTesting';
import SecurityTesting from './pages/services/SecurityTesting';
import MobileTesting from './pages/services/MobileTesting';
import AutomationTesting from './pages/services/AutomationTesting';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import FreeTrial from './pages/FreeTrial';
import TermsOfService from './pages/TermsOfService';
import Privacy from './pages/Privacy';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { Link as ScrollLink } from 'react-scroll';

// Styled Components
const AppContainer = styled.div`
  font-family: 'Inter', sans-serif;
  color: #1a1a1a;
`;

const PageContainer = styled.div`
  padding-top: 80px;
`;

const Hero = styled.section`
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 30%, rgba(37, 99, 235, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(37, 99, 235, 0.05) 0%, transparent 50%),
      url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.8;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroBadge = styled(motion.span)`
  display: inline-block;
  padding: 0.6rem 1.2rem;
  background: white;
  color: #1e40af;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(37, 99, 235, 0.2);
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.05);
`;

const Headline = styled(motion.h1)`
  font-size: 4.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.1;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const UnderlinedText = styled.span`
  position: relative;
  white-space: nowrap;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 100%;
    height: 3px;
    background-image: url("data:image/svg+xml,%3Csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0,1 Q25,0 50,2 T100,1' stroke='%23ef4444' stroke-width='2' fill='none' stroke-linecap='round' vector-effect='non-scaling-stroke' /%3E%3C/svg%3E");
    background-repeat: repeat-x;
    background-size: 100px 100%;
    opacity: 0.8;
    transform: rotate(-0.5deg);
  }
`;

const Subheadline = styled(motion.p)`
  font-size: 1.35rem;
  line-height: 1.7;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: #475569;
  font-weight: 400;
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-bottom: 4rem;
`;

const PrimaryButton = styled(motion.button)`
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  color: white;
  padding: 1.2rem 2.5rem;
  border-radius: 50px;
  border: none;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.1),
              0 10px 20px rgba(37, 99, 235, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(37, 99, 235, 0.2),
                0 12px 24px rgba(37, 99, 235, 0.2);
  }
`;

const SecondaryButton = styled(motion.button)`
  background: white;
  color: #2563eb;
  padding: 1.2rem 2.5rem;
  border-radius: 50px;
  border: 2px solid rgba(37, 99, 235, 0.2);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

  &:hover {
    background: rgba(37, 99, 235, 0.05);
    border-color: #2563eb;
    transform: translateY(-2px);
  }
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 6rem;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(37, 99, 235, 0.1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const StatItem = styled(motion.div)`
  text-align: center;
  position: relative;
  
  &:not(:last-child):after {
    content: '';
    position: absolute;
    right: -3rem;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 40px;
    background: rgba(37, 99, 235, 0.2);
  }
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;
`;

const StatLabel = styled.div`
  color: #475569;
  font-size: 1rem;
  font-weight: 500;
`;

const Section = styled.section`
  padding: ${props => props.dark ? '3rem 2rem 6rem' : '6rem 2rem'};
  background: ${props => props.dark ? `
    linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%),
    radial-gradient(circle at 20% 30%, rgba(37, 99, 235, 0.07) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(37, 99, 235, 0.07) 0%, transparent 50%),
    url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
  ` : 'white'};
  position: relative;
  overflow: hidden;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 3rem;
  line-height: 1.6;
`;

const AuditFormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 24px;
  box-shadow: 
    0 4px 6px rgba(37, 99, 235, 0.05),
    0 10px 15px rgba(37, 99, 235, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(37, 99, 235, 0.1);
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.5), rgba(37, 99, 235, 0.1));
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #1e293b;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: -0.01em;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(37, 99, 235, 0.1);
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.9);
  
  &::placeholder {
    color: #94a3b8;
  }

  &:hover {
    border-color: rgba(37, 99, 235, 0.3);
  }

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
    background: white;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(37, 99, 235, 0.1);
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.9);
  color: #1e293b;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%232563eb' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1.2em;
  padding-right: 2.5rem;
  font-weight: 500;

  &:invalid {
    color: #94a3b8;
  }

  option {
    color: #1e293b;
    background: white;
    font-weight: 500;
    padding: 0.75rem;
  }

  option:disabled {
    color: #94a3b8;
  }

  &:hover {
    border-color: rgba(37, 99, 235, 0.3);
    background-color: rgba(255, 255, 255, 1);
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.05);
  }

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
    background-color: white;
  }
`;

const SuccessMessage = styled(motion.div)`
  text-align: center;
  color: #059669;
  background: rgba(5, 150, 105, 0.1);
  padding: 1.25rem;
  border-radius: 12px;
  margin-top: 1.5rem;
  border: 1px solid rgba(5, 150, 105, 0.2);
  font-weight: 500;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const FeatureCard = styled(motion.div)`
  padding: 2.5rem;
  background: white;
  border-radius: 24px;
  transition: all 0.3s ease;
  border: 1px solid rgba(37, 99, 235, 0.1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.03) 0%, rgba(37, 99, 235, 0) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(37, 99, 235, 0.3);
    box-shadow: 
      0 4px 6px rgba(37, 99, 235, 0.05),
      0 10px 15px rgba(37, 99, 235, 0.1);

    &:before {
      opacity: 1;
    }
  }
`;

const FeatureIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.2), rgba(37, 99, 235, 0.1));
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FeatureDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  font-size: 1.1rem;
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 4rem auto;
  padding: 0 1rem;
`;

const TestimonialCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(37, 99, 235, 0.1);
  }
`;

const TestimonialText = styled.p`
  color: #1e293b;
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.2rem;
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.div`
  font-weight: 600;
  color: #1e293b;
`;

const AuthorRole = styled.div`
  color: #64748b;
  font-size: 0.9rem;
`;

const TrustBadges = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
  margin-top: 3rem;
  flex-wrap: wrap;
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #64748b;
  font-size: 0.9rem;
`;

const FormButton = styled(PrimaryButton)`
  width: 100%;
  padding: 1.25rem;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  font-size: 1.1rem;
  letter-spacing: -0.01em;
  box-shadow: 
    0 4px 6px rgba(37, 99, 235, 0.1),
    0 10px 20px rgba(37, 99, 235, 0.1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 6px 8px rgba(37, 99, 235, 0.2),
      0 12px 24px rgba(37, 99, 235, 0.2);
  }
`;

const ScrollButton = styled(ScrollLink)`
  display: inline-block;
  text-decoration: none;
`;

function HomePage() {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    jobTitle: '',
    website: '',
    monthlyOrders: ''
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the data to your backend
    setShowSuccess(true);
    setFormData({ name: '', email: '', phone: '', jobTitle: '', website: '', monthlyOrders: '' });
    setTimeout(() => setShowSuccess(false), 5000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <PageContainer>
      <Hero>
        <HeroContent
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroBadge
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            Trusted by 500+ Shopify Plus Stores
          </HeroBadge>
          <Headline>
            AI-Powered Testing for
            <br />
            Shopify Excellence
          </Headline>
          <Subheadline>
            <UnderlinedText>Prevent lost sales</UnderlinedText> and protect your revenue with our intelligent testing platform.
            Catch issues before they impact your customers and ensure a flawless shopping experience.
          </Subheadline>
          <HeroButtons>
            <ScrollButton
              to="auditForm"
              smooth={true}
              duration={800}
              offset={-100}
            >
              <PrimaryButton
                as="div"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Your Free Audit
              </PrimaryButton>
            </ScrollButton>
            <Link to="/pricing" style={{ textDecoration: 'none' }}>
              <SecondaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View Pricing
              </SecondaryButton>
            </Link>
          </HeroButtons>
          <StatsContainer>
            <StatItem
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <StatNumber>50K+</StatNumber>
              <StatLabel>Tests Run Daily</StatLabel>
            </StatItem>
            <StatItem
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <StatNumber>99.9%</StatNumber>
              <StatLabel>Uptime Guaranteed</StatLabel>
            </StatItem>
            <StatItem
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <StatNumber>$2M+</StatNumber>
              <StatLabel>Revenue Protected</StatLabel>
            </StatItem>
          </StatsContainer>
        </HeroContent>
      </Hero>

      <Section ref={ref}>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          Why Choose Revguard?
        </SectionTitle>
        <SectionSubtitle>
          We help Shopify stores catch issues before they impact customers.
        </SectionSubtitle>
        <FeatureGrid>
          {[
            {
              icon: '⚡️',
              title: 'Checkout & Payment Testing',
              description: 'Never lose a sale due to a broken checkout.',
              path: '/services/checkout'
            },
            {
              icon: '🚀',
              title: 'Speed & Performance Testing',
              description: 'Boost site speed for higher conversions.',
              path: '/services/performance'
            },
            {
              icon: '🔌',
              title: 'Third-Party App & Integration Testing',
              description: 'Ensure all your apps work seamlessly.',
              path: '/services/integration'
            },
            {
              icon: '🛡️',
              title: 'Shopify Update Protection',
              description: 'Test every update before it breaks your store.',
              path: '/services/security'
            },
            {
              icon: '📈',
              title: 'Scale Fearlessly: Peak Traffic Testing',
              description: 'Handle high-volume traffic with confidence through automated catalog testing.',
              path: '/services/automation'
            },
            {
              icon: '📱',
              title: 'Cross Device Testing',
              description: 'Ensure seamless shopping experience across all devices and browsers.',
              path: '/services/mobile'
            }
          ].map((feature, index) => (
            <Link to={feature.path} key={index} style={{ textDecoration: 'none', color: 'inherit' }}>
              <FeatureCard
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <div>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </div>
              </FeatureCard>
            </Link>
          ))}
        </FeatureGrid>
      </Section>

      <Section>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          What Our Clients Say
        </SectionTitle>
        <TestimonialGrid>
          <TestimonialCard>
            <TestimonialText>
              "Since implementing Revguard, we've seen a 40% reduction in checkout-related support tickets and a significant increase in conversion rates."
            </TestimonialText>
            <TestimonialAuthor>
              <AuthorAvatar>JS</AuthorAvatar>
              <AuthorInfo>
                <AuthorName>John Smith</AuthorName>
                <AuthorRole>CTO at SportGear Plus</AuthorRole>
              </AuthorInfo>
            </TestimonialAuthor>
          </TestimonialCard>
          <TestimonialCard>
            <TestimonialText>
              "The automated testing has saved our team countless hours and helped us maintain 99.9% uptime during peak sales events."
            </TestimonialText>
            <TestimonialAuthor>
              <AuthorAvatar>SK</AuthorAvatar>
              <AuthorInfo>
                <AuthorName>Sarah Kim</AuthorName>
                <AuthorRole>Head of E-commerce at FashionCo</AuthorRole>
              </AuthorInfo>
            </TestimonialAuthor>
          </TestimonialCard>
        </TestimonialGrid>
      </Section>

      <Section dark id="auditForm">
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          Get Your Free Checkout Audit
        </SectionTitle>
        <SectionSubtitle>
          Discover hidden issues that could be costing you sales. No commitment required.
        </SectionSubtitle>
        <AuditFormContainer>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="name">Full Name</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Smith"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="email">Work Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@yourstore.com"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+1 (555) 000-0000"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="jobTitle">Job Title</Label>
              <Input
                type="text"
                id="jobTitle"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleChange}
                required
                placeholder="E-commerce Manager"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="website">Shopify Store URL</Label>
              <Input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                required
                placeholder="https://yourstore.myshopify.com"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="monthlyOrders">Monthly Orders</Label>
              <Select
                id="monthlyOrders"
                name="monthlyOrders"
                value={formData.monthlyOrders}
                onChange={handleChange}
                required
              >
                <option value="" disabled>Select monthly order volume</option>
                <option value="0-10K">0 - 10K</option>
                <option value="10K-50K">10K - 50K</option>
                <option value="50K+">50K plus</option>
              </Select>
            </FormGroup>
            <FormButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Free Audit
            </FormButton>
          </form>
          {showSuccess && (
            <SuccessMessage
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              Thanks! We'll be in touch within 24 hours with your audit results.
            </SuccessMessage>
          )}
          <TrustBadges>
            <TrustBadge>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 6c1.4 0 2.8 1.1 2.8 2.5V11c.6 0 1.2.6 1.2 1.3v3.5c0 .6-.6 1.2-1.3 1.2H9.3C8.6 17 8 16.4 8 15.7v-3.5c0-.6.6-1.2 1.2-1.2V9.5C9.2 8.1 10.6 7 12 7zm0 1.2c-.8 0-1.5.5-1.5 1.3V11h3V9.5c0-.8-.7-1.3-1.5-1.3z"/>
              </svg>
              Enterprise-grade security
            </TrustBadge>
            <TrustBadge>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
              </svg>
              Same-day response
            </TrustBadge>
          </TrustBadges>
        </AuditFormContainer>
      </Section>
    </PageContainer>
  );
}

function App() {
  return (
    <Router>
      <AppContainer>
        <Navbar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/checkout" element={<CheckoutTesting />} />
          <Route path="/services/performance" element={<PerformanceTesting />} />
          <Route path="/services/integration" element={<IntegrationTesting />} />
          <Route path="/services/security" element={<SecurityTesting />} />
          <Route path="/services/mobile" element={<MobileTesting />} />
          <Route path="/services/automation" element={<AutomationTesting />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/free-trial" element={<FreeTrial />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<Privacy />} />
        </Routes>
        <Footer />
      </AppContainer>
    </Router>
  );
}

export default App;